import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  PackageCheck,
  Printer,
  Truck,
  X,
} from 'lucide-react'
import { apiGet, apiPost, formatMXN } from '../lib/api'
import { Badge } from './Badge'

// -----------------------------------------------------------------------------
// Tipos del contrato GET /api/admin/pedidos/{orderId}/envio
// -----------------------------------------------------------------------------

export interface EnvioSummaryItem {
  orderItemId: string
  sku: string
  name: string
  ordered: number
  shipped: number
  remaining: number
}

export interface EnvioShipment {
  id: string
  carrier: string
  provider: string
  service: string
  trackingNumber: string
  trackingUrl?: string | null
  status: string
  costCents: number
  carrierCostCents?: number | null
  weightKg?: number | null
  lengthCm?: number | null
  widthCm?: number | null
  heightCm?: number | null
  createdAt: string
  errorMessage?: string | null
  items: Array<{ orderItemId: string; sku: string; name: string; quantity: number }>
}

export interface EnvioSummary {
  items: EnvioSummaryItem[]
  shipments: EnvioShipment[]
  fullyShipped: boolean
}

interface EnvioResponse {
  summary: EnvioSummary | null
}

interface GenerarResponse {
  ok: boolean
  shipment: EnvioShipment
}

export interface ShippingOverride {
  street: string
  number: string
  neighborhood: string
  city: string
  state: string
  zip: string
  phone: string
}

const SHIPMENT_STATUS: Record<
  string,
  { label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' }
> = {
  pendiente: { label: 'Pendiente', variant: 'warning' },
  autorizada: { label: 'Autorizada', variant: 'info' },
  en_transito: { label: 'En tránsito', variant: 'info' },
  entregada: { label: 'Entregada', variant: 'success' },
  error: { label: 'Error', variant: 'danger' },
  cancelada: { label: 'Cancelada', variant: 'default' },
}

function shipmentStatusBadge(status: string) {
  const st = SHIPMENT_STATUS[status] ?? { label: status, variant: 'default' as const }
  return <Badge variant={st.variant}>{st.label}</Badge>
}

interface SelectionState {
  checked: boolean
  qty: number
}

/**
 * Panel de generación de guías POR PRODUCTO con envíos parciales.
 * Compartido entre la cola de "Autorizar envíos" y el detalle del pedido.
 *
 * - Muestra los productos con su cantidad pedida / enviada / restante.
 * - Permite seleccionar productos + cantidad y generar una guía que cubre
 *   SOLO esa selección (envío parcial). Sin selección explícita se manda todo
 *   lo que falte.
 * - Lista las guías generadas con tracking, estado e impresión de etiqueta.
 */
export function EnvioPanel({
  orderId,
  showAddress = false,
  initialCarrier = 'estafeta',
  initialAddress,
  onGenerated,
}: {
  orderId: string
  /** Renderiza los campos editables de dirección (shippingOverride). */
  showAddress?: boolean
  initialCarrier?: 'estafeta' | 'fedex'
  initialAddress?: Partial<ShippingOverride>
  onGenerated?: () => void
}) {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery<EnvioResponse>({
    queryKey: ['pedido-envio', orderId],
    queryFn: () => apiGet(`/api/admin/pedidos/${orderId}/envio`),
    refetchOnWindowFocus: false,
  })

  const summary = data?.summary ?? null

  const [carrier, setCarrier] = useState<'' | 'estafeta' | 'fedex'>(initialCarrier)
  const [override, setOverride] = useState<ShippingOverride>({
    street: initialAddress?.street || '',
    number: initialAddress?.number || '',
    neighborhood: initialAddress?.neighborhood || '',
    city: initialAddress?.city || '',
    state: initialAddress?.state || '',
    zip: initialAddress?.zip || '',
    phone: initialAddress?.phone || '',
  })
  const [selection, setSelection] = useState<Record<string, SelectionState>>({})
  const [error, setError] = useState<string | null>(null)
  // Guía recién generada → muestra el modal "Guía generada" (Ver / Generar otra).
  const [justGenerated, setJustGenerated] = useState<EnvioShipment | null>(null)

  // Sincroniza la selección cada vez que llega/actualiza el summary:
  // marca por default todos los productos con remaining > 0 a su máximo.
  useEffect(() => {
    if (!summary) return
    const next: Record<string, SelectionState> = {}
    for (const it of summary.items) {
      if (it.remaining > 0) next[it.orderItemId] = { checked: true, qty: it.remaining }
    }
    setSelection(next)
  }, [summary])

  const generateMut = useMutation<GenerarResponse, Error>({
    mutationFn: () => {
      const items = (summary?.items ?? [])
        .filter((it) => it.remaining > 0 && selection[it.orderItemId]?.checked)
        .map((it) => ({
          orderItemId: it.orderItemId,
          quantity: Math.min(Math.max(1, selection[it.orderItemId]?.qty ?? it.remaining), it.remaining),
        }))
      const body: {
        items: Array<{ orderItemId: string; quantity: number }>
        carrier?: 'estafeta' | 'fedex'
        shippingOverride?: ShippingOverride
      } = { items }
      if (carrier) body.carrier = carrier
      if (showAddress) body.shippingOverride = override
      return apiPost<GenerarResponse>(`/api/admin/pedidos/${orderId}/envio/generar`, body)
    },
    onSuccess: (data) => {
      setError(null)
      if (data?.shipment) setJustGenerated(data.shipment)
      queryClient.invalidateQueries({ queryKey: ['pedido-envio', orderId] })
      queryClient.invalidateQueries({ queryKey: ['pedido', orderId] })
      onGenerated?.()
    },
    onError: (e) => setError(e.message),
  })

  function setField(key: keyof ShippingOverride, value: string) {
    setOverride((prev) => ({ ...prev, [key]: value }))
  }

  function toggle(orderItemId: string, remaining: number) {
    setSelection((prev) => {
      const cur = prev[orderItemId] ?? { checked: false, qty: remaining }
      return { ...prev, [orderItemId]: { ...cur, checked: !cur.checked } }
    })
  }

  function setQty(orderItemId: string, value: number, remaining: number) {
    const qty = Math.min(Math.max(1, value || 1), remaining)
    setSelection((prev) => ({
      ...prev,
      [orderItemId]: { checked: prev[orderItemId]?.checked ?? true, qty },
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-ink-500">
        <Loader2 className="size-4 animate-spin" /> Cargando envío…
      </div>
    )
  }

  if (!summary) {
    return (
      <p className="py-2 text-sm text-ink-500">
        No hay información de envío para este pedido.
      </p>
    )
  }

  const pendientes = summary.items.filter((it) => it.remaining > 0)
  const nSelected = pendientes.filter((it) => selection[it.orderItemId]?.checked).length

  return (
    <div className="space-y-4">
      {/* Estado global */}
      <div className="flex items-center gap-2">
        {summary.fullyShipped ? (
          <Badge variant="success">
            <PackageCheck className="size-3" /> Todo enviado
          </Badge>
        ) : (
          <Badge variant="warning">
            {pendientes.length} {pendientes.length === 1 ? 'producto' : 'productos'} por enviar
          </Badge>
        )}
      </div>

      {/* Productos + selección para envío parcial */}
      {pendientes.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-ink-200 bg-white">
          <div className="border-b border-ink-100 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
            Productos por enviar
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-ink-500">
                  <th className="w-8 px-3 py-2" />
                  <th className="px-3 py-2 font-medium">Producto</th>
                  <th className="px-3 py-2 font-medium">SKU</th>
                  <th className="px-3 py-2 text-right font-medium">Pedido</th>
                  <th className="px-3 py-2 text-right font-medium">Enviado</th>
                  <th className="px-3 py-2 text-right font-medium">Restante</th>
                  <th className="px-3 py-2 text-right font-medium">Cantidad a enviar</th>
                </tr>
              </thead>
              <tbody>
                {summary.items.map((it) => {
                  const done = it.remaining <= 0
                  const sel = selection[it.orderItemId]
                  return (
                    <tr
                      key={it.orderItemId}
                      className={`border-t border-ink-100 ${done ? 'opacity-50' : ''}`}
                    >
                      <td className="px-3 py-2">
                        {done ? (
                          <CheckCircle2 className="size-4 text-emerald-500" />
                        ) : (
                          <input
                            type="checkbox"
                            checked={sel?.checked ?? false}
                            onChange={() => toggle(it.orderItemId, it.remaining)}
                            className="size-4 rounded border-ink-300"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 text-ink-900">{it.name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-ink-600">{it.sku}</td>
                      <td className="px-3 py-2 text-right text-ink-700">{it.ordered}</td>
                      <td className="px-3 py-2 text-right text-ink-700">{it.shipped}</td>
                      <td className="px-3 py-2 text-right font-semibold text-ink-900">
                        {it.remaining}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {done ? (
                          <span className="text-xs text-emerald-600">Completo</span>
                        ) : (
                          <input
                            type="number"
                            min={1}
                            max={it.remaining}
                            value={sel?.qty ?? it.remaining}
                            disabled={!sel?.checked}
                            onChange={(e) =>
                              setQty(it.orderItemId, parseInt(e.target.value || '1'), it.remaining)
                            }
                            className="w-16 rounded border border-ink-200 px-2 py-1 text-right text-sm outline-none focus:border-brand-500 disabled:bg-ink-50 disabled:text-ink-400"
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dirección editable (solo en la cola de envíos) */}
      {showAddress && pendientes.length > 0 && (
        <div className="rounded-xl border border-ink-100 bg-ink-50 p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
            Revisar / editar dirección de entrega
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <Field label="Calle" value={override.street} onChange={(v) => setField('street', v)} />
            <Field label="Número" value={override.number} onChange={(v) => setField('number', v)} />
            <Field
              label="Colonia"
              value={override.neighborhood}
              onChange={(v) => setField('neighborhood', v)}
            />
            <Field label="Ciudad" value={override.city} onChange={(v) => setField('city', v)} />
            <Field label="Estado" value={override.state} onChange={(v) => setField('state', v)} />
            <Field label="CP" value={override.zip} onChange={(v) => setField('zip', v)} />
            <Field label="Teléfono" value={override.phone} onChange={(v) => setField('phone', v)} />
          </div>
        </div>
      )}

      {/* Carrier + generar */}
      {pendientes.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-ink-500">Transportista</label>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value as '' | 'estafeta' | 'fedex')}
              className="rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-brand-500"
              title="Transportista"
            >
              <option value="">Automático</option>
              <option value="estafeta">Estafeta</option>
              <option value="fedex">FedEx</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => generateMut.mutate()}
            disabled={generateMut.isPending || nSelected === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {generateMut.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Truck className="size-4" />
            )}
            Generar guía
            {nSelected > 0 ? ` (${nSelected})` : ''}
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Guías generadas */}
      {summary.shipments.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-500">
            Guías generadas ({summary.shipments.length})
          </div>
          <ul className="space-y-2">
            {summary.shipments.map((s) => (
              <li
                key={s.id}
                className={`rounded-xl border p-3 ${
                  s.status === 'error' ? 'border-red-200 bg-red-50' : 'border-ink-200 bg-white'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {shipmentStatusBadge(s.status)}
                    <span className="text-sm font-medium text-ink-900">
                      {s.provider || s.carrier || '—'}
                    </span>
                    {s.service && (
                      <span className="text-xs text-ink-500">
                        · <span className="text-ink-400">Tipo:</span> {s.service}
                      </span>
                    )}
                    {s.costCents > 0 && (
                      <span className="text-xs text-ink-500">· {formatMXN(s.costCents / 100)}</span>
                    )}
                  </div>
                  {s.status !== 'error' && (
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          `/api/admin/pedidos/${orderId}/envio/etiqueta?shipmentId=${s.id}`,
                          '_blank',
                        )
                      }
                      className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50"
                    >
                      <Printer className="size-3.5" /> Imprimir etiqueta
                    </button>
                  )}
                </div>

                {s.trackingNumber && (
                  <div className="mt-1.5 text-xs text-ink-600">
                    Tracking:{' '}
                    {s.trackingUrl ? (
                      <a
                        href={s.trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-brand-600 hover:underline"
                      >
                        {s.trackingNumber}
                      </a>
                    ) : (
                      <span className="font-mono">{s.trackingNumber}</span>
                    )}
                  </div>
                )}

                {s.status !== 'error' &&
                  (s.weightKg != null || s.lengthCm != null || s.carrierCostCents != null) && (
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-ink-500">
                      {s.lengthCm != null && s.widthCm != null && s.heightCm != null && (
                        <span>
                          Medidas: {s.lengthCm}×{s.widthCm}×{s.heightCm} cm
                        </span>
                      )}
                      {s.weightKg != null && <span>Peso: {s.weightKg} kg</span>}
                      {s.carrierCostCents != null && (
                        <span>
                          Costo real transportista:{' '}
                          <strong className="text-ink-700">{formatMXN(s.carrierCostCents / 100)}</strong>
                        </span>
                      )}
                      <span>Cobrado al cliente: {formatMXN(s.costCents / 100)}</span>
                    </div>
                  )}

                {s.status === 'error' && s.errorMessage && (
                  <div className="mt-1.5 flex items-start gap-1.5 text-xs text-red-700">
                    <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                    <span>{s.errorMessage}</span>
                  </div>
                )}

                {s.items.length > 0 && (
                  <ul className="mt-2 space-y-0.5 border-t border-ink-100 pt-2 text-xs text-ink-600">
                    {s.items.map((it) => (
                      <li key={it.orderItemId} className="flex justify-between gap-2">
                        <span className="truncate">
                          <span className="font-mono text-ink-500">{it.sku}</span> · {it.name}
                        </span>
                        <span className="shrink-0 font-medium text-ink-800">×{it.quantity}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Faltan por enviar */}
      {pendientes.length > 0 && (
        <div className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <span className="font-semibold">Faltan por enviar: </span>
          {pendientes.map((it, i) => (
            <span key={it.orderItemId}>
              {i > 0 ? ', ' : ''}
              {it.name} ×{it.remaining}
            </span>
          ))}
        </div>
      )}

      {/* Modal: guía generada → Ver guía / Generar otra */}
      {justGenerated && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setJustGenerated(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-6 text-emerald-500" />
                <h3 className="text-base font-semibold text-ink-900">Guía generada</h3>
              </div>
              <button
                onClick={() => setJustGenerated(null)}
                className="rounded-md p-1 text-ink-400 hover:bg-ink-100"
                title="Cerrar"
              >
                <X className="size-4" />
              </button>
            </div>
            <p className="mt-2 text-sm text-ink-600">
              {justGenerated.provider || justGenerated.carrier}
              {justGenerated.service ? ` · ${justGenerated.service}` : ''}
            </p>
            {justGenerated.trackingNumber && (
              <p className="mt-1 text-xs text-ink-500">
                Tracking: <span className="font-mono">{justGenerated.trackingNumber}</span>
              </p>
            )}
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  window.open(
                    `/api/admin/pedidos/${orderId}/envio/etiqueta?shipmentId=${justGenerated.id}`,
                    '_blank',
                  )
                  setJustGenerated(null)
                }}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
              >
                <Printer className="size-4" /> Ver guía
              </button>
              <button
                onClick={() => setJustGenerated(null)}
                className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                Generar otra
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-ink-500">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-brand-500"
      />
    </label>
  )
}
